import { useState, useCallback, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Notification } from "@rio-cloud/rio-uikit";

import { IDinamicSmartRoutePreferences } from "src/components/Dynamics/DinamicSmartRoutePreferences";
import { ISuggestion } from "./type";
import { fetchSmartRoutePreferences, ISmartRoutePreferenceRequest } from "src/services/smartRoutes";
import { sendTripCustom, sendTripUpdateCopy } from "src/services/trips";
import { clearTrip, useAppSelector } from "@store";
import { useDispatch } from "react-redux";
import { buildLinkedVehicleItem, formatDateInUtc, formatDurationToMinutes, safeFormatDateEn } from "@utils";
import { createAndSendRoute, updateRouteForTrip } from "src/services/routes";
import { createRouteCost } from "src/services/costSimulator";
import { defineAreasRework, defineAvoidAreas } from "src/store/routes/RoutesSlice";
import { updateAreaRework } from "src/services/areas";
import { useAreaRoute } from "@hooks";

export const useControll = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [isCreatingTrip, setIsCreatingTrip] = useState(false);

  const { smartRoutePreference, routeData, body, navigateTo, csv, edit, selectedFuelOption } =
    (state as {
      smartRoutePreference: IDinamicSmartRoutePreferences;
      body: any;
      csv: boolean;
      edit: boolean;
      selectedFuelOption: number;
      navigateTo?: {
        route: string;
        params?: any;
      };
      routeData: {
        avoidToll: boolean;
        origin: any;
        destination: any;
        vehicle: any;
        stops: any[];
        routeName: string;
        accountId: string;
        tripId: string;
        routeCost: any;
        distance: string;
        duration: string;
        responseHere: any;
      };
    }) ?? {};

  const [suggestions, setSuggestions] = useState<ISuggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const { areasToAvoid } = useAppSelector((state) => state.routes);

  const handlerLoadSuggestions = useCallback(async () => {
    if (!smartRoutePreference || !routeData) return;

    setLoading(true);

    try {
      const requestBody: ISmartRoutePreferenceRequest = {
        origin: `${routeData.origin.lat},${routeData.origin.lng}`,
        destination: `${routeData.destination.lat},${routeData.destination.lng}`,
        roads: routeData.stops.map((s) => `${s.position.lat},${s.position.lng}`),
        strategies: ["all"],
        avoidToll: routeData.avoidToll,
        avoidAreas: areasToAvoid?.formatted ?? "",
        vehicle: {
          height: routeData.vehicle.height || 0,
          width: routeData.vehicle.width || 0,
          length: routeData.vehicle.comTotal || 0,
          numberOfAxles: routeData.vehicle.numberOfAxles || 0,
          trailerAxles: routeData.vehicle.trailerAxles || 0,
          grossWeight: routeData.vehicle.grossWeight || 0,
          shippedHazardousGoods: routeData.vehicle.shippedHazardousGoods || "NONE",
        },
        transport: {
          mode: routeData.vehicle.type,
        },
        departureTime: new Date().toISOString(),
        fuel_rate: routeData.routeCost.averageConsume,
        operationalCost: routeData.routeCost.operativeCosts,
        fuelPrice: routeData.routeCost.fuelAverageCosts,
        engine_speed: 0,
        mileage: 0,
        timeWeight: 0,
        original_route: {
          distance: parseFloat(routeData.distance) * 1000,
          duration: formatDurationToMinutes(routeData.duration),
          total_cost: routeData.routeCost.totalCosts,
        },
      };

      const response = await fetchSmartRoutePreferences(requestBody);
      const order = ["lowest", "lowest_notoll", "fastest", "fastest_notoll", "cheapest", "cheapest_notoll"];

      const loadedSuggestions = response?.sort((a, b) => {
        return order.indexOf(a.strategy) - order.indexOf(b.strategy);
      });

      if (loadedSuggestions.length === 0) {
        Notification.error("Não foi possível encontrar rotas para as preferências selecionadas.");
      }

      setSuggestions(loadedSuggestions);
    } catch (error: any) {
      console.log("error >>>>>>", error);
      Notification.error(error.message || "Erro ao carregar sugestões de rota inteligente");
    } finally {
      setLoading(false);
    }
  }, [smartRoutePreference, routeData, navigate]);

  useEffect(() => {
    handlerLoadSuggestions();
  }, [handlerLoadSuggestions]);

  const formatHours = (totalSeconds: number) => {
    if (typeof totalSeconds !== "number" || totalSeconds < 0 || isNaN(totalSeconds)) {
      return "0h 0min 0s";
    }
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    return `${hours}h${minutes}min`;
  };

  const dispatch = useDispatch();
  const { saveAreaNewRoute } = useAreaRoute();

  const {
    driversSelected,
    nameVehicleSelected,
    idVehicleSelected,
    scheduleDateSelected,
    scheduleHoursSelected,
    route: routeStore,
    backup,
  } = useAppSelector((state) => state.trips);

  const { routeCost: routeCostStore, areasRework, areasCopyRework } = useAppSelector((state) => state.routes);

  const handleSelectSuggestion = useCallback(
    async (suggestion: ISuggestion) => {
      if (isCreatingTrip) return;
      setIsCreatingTrip(true);

      const strategy = suggestion?.strategy;
      const isEditFlow = Boolean(routeData?.tripId || edit);

      try {
        const sendParams = {
          ...body,
          responseHere: { routes: suggestion.completed_route?.routes || [] },
          duration: formatHours(suggestion.completed_route?.travelSummary?.duration || 0),
          distance: suggestion?.completed_route?.travelSummary?.length
            ? `${(suggestion?.completed_route?.travelSummary?.length / 1000)?.toFixed(2)?.replace(/\./g, ",")}km`
            : "0km",
          strategy: strategy,
          costs: {
            ...body.costs,
            totalCosts: suggestion.total_cost,
          },
        };

        if (isEditFlow && routeData.tripId) {
          const linkedVehicleItem = buildLinkedVehicleItem(
            {
              assetId: idVehicleSelected as string,
              assetName: nameVehicleSelected as string,
              routeId: (routeStore?.id || body?.id) as string,
              startDateTime: `${scheduleDateSelected}T${scheduleHoursSelected}:00+00:00`,
              startDateTimeUTC: formatDateInUtc(`${scheduleDateSelected}T${scheduleHoursSelected}`),
            },
            driversSelected
          );
          const payload = {
            ...sendParams,
            linkedVehicles: driversSelected.length > 0 ? [linkedVehicleItem] : sendParams.linkedVehicles,
          };
          const response = await sendTripUpdateCopy(payload, routeData.tripId);

          const updatedRouteCost = {
            ...routeCostStore,
            totalCost: suggestion.total_cost,
            route: response.data.result?.newRouteId,
            routeId: response.data.result?.newRouteId,
          };

          await saveAreaNewRoute(response.data.result?.newRouteId, areasRework, areasCopyRework);
          await createRouteCost(updatedRouteCost);

          Notification.success(`Rota editada com sucesso.`);
        } else if (!body.id) {
          await CreateTripCSVNoSuggestion(sendParams, suggestion?.strategy);
          return;
        } else {
          const update = {
            ...body,
            responseHere: { routes: suggestion.completed_route?.routes || [] },
            duration: formatHours(suggestion.completed_route?.travelSummary?.duration || 0),
            distance: suggestion?.completed_route?.travelSummary?.length
              ? `${(suggestion?.completed_route?.travelSummary?.length / 1000)?.toFixed(2)?.replace(/\./g, ",")}km`
              : "0km",
            strategy: strategy,
            routeName: body?.routeName + " - PERSONALIZADO",
            costs: {
              ...body.costs,
              totalCosts: suggestion.total_cost,
            },
          };

          const response = await updateRouteForTrip(update);

          const updatedRouteCost = {
            ...routeCostStore,
            totalCost: suggestion.total_cost,
            route: response.data.result,
            routeId: response.data.result,
          };

          const linkedVehicleItem = buildLinkedVehicleItem(
            {
              assetId: idVehicleSelected as string,
              assetName: nameVehicleSelected as string,
              routeId: response?.data.result as string,
              startDateTime: `${scheduleDateSelected}T${scheduleHoursSelected}:00+00:00`,
              startDateTimeUTC: formatDateInUtc(`${scheduleDateSelected}T${scheduleHoursSelected}`),
            },
            driversSelected
          );

          const bodyTrip = {
            linkedVehicles: [linkedVehicleItem],
          };

          await createRouteCost(updatedRouteCost);
          await sendTripCustom(bodyTrip, body.id as string);
          await saveAreaNewRoute(response?.data.result, areasRework);
          Notification.success(`Rota criada com sucesso.`);
        }

        dispatch(defineAvoidAreas({ formatted: undefined }));
        dispatch(defineAreasRework([]));
        navigate("/routing/trips");
      } catch (error: any) {
        if (error?.response?.data?.message) {
          Notification.error(error?.response?.data?.message || "Motorista com viagem neste período.");
        } else {
          Notification.error("Não foi possível enviar a viagem.");
        }
      } finally {
        setIsCreatingTrip(false);
      }
    },
    [smartRoutePreference, routeData, navigate, navigateTo, isCreatingTrip, edit]
  );

  const CreateTripNoSuggestion = async (id?: string) => {
    const idRoute = id || (body.id as string);

    if (isCreatingTrip) return;
    setIsCreatingTrip(true);
    try {
      const response = await updateRouteForTrip({
        ...routeStore,
        id: idRoute,
        routeName: routeStore?.routeName + " - PERSONALIZADO",
        strategy: null,
      });

      if (response?.status === "success" && routeCostStore?.totalCost > 0) {
        const updatedRouteCost = {
          ...routeCostStore,
          route: response.data.result,
          routeId: response.data.result,
        };
        await createRouteCost(updatedRouteCost);
      }

      const responseId = response?.data?.result;

      const linkedVehicleItem = buildLinkedVehicleItem(
        {
          assetId: idVehicleSelected as string,
          assetName: nameVehicleSelected as string,
          routeId: responseId as string,
          startDateTime: `${scheduleDateSelected}T${scheduleHoursSelected}:00+00:00`,
          startDateTimeUTC: formatDateInUtc(`${scheduleDateSelected}T${scheduleHoursSelected}`),
        },
        driversSelected
      );

      const bodyTrip = {
        linkedVehicles: [linkedVehicleItem],
      };

      await sendTripCustom(bodyTrip, idRoute || responseId);
      await saveAreaNewRoute(responseId, areasRework);

      Notification.success(`Rota sem otimização criada com sucesso.`);
      dispatch(clearTrip());
      dispatch(defineAvoidAreas({ formatted: undefined }));
      dispatch(defineAreasRework([]));
      navigate("/routing/trips");
    } catch (error: any) {
      if (error?.response?.data?.message) {
        Notification.error(error?.response?.data?.message || "Motorista com viagem neste período.");
      } else {
        Notification.error("Não foi possível enviar a viagem.");
      }
    } finally {
      setIsCreatingTrip(false);
    }
  };

  const EditTripNoSuggestion = async () => {
    if (!routeStore && !body) return;
    if (!routeData?.tripId) return;
    if (isCreatingTrip) return;
    setIsCreatingTrip(true);

    const routePayload = routeStore ?? body;
    const linkedVehicleItem = buildLinkedVehicleItem(
      {
        assetId: idVehicleSelected as string,
        assetName: nameVehicleSelected as string,
        routeId: (routePayload?.id || body?.id) as string,
        startDateTime: `${scheduleDateSelected}T${scheduleHoursSelected}:00+00:00`,
        startDateTimeUTC: formatDateInUtc(`${scheduleDateSelected}T${scheduleHoursSelected}`),
      },
      driversSelected
    );

    const payloadDefault = {
      ...routePayload,
      strategy: null,
    };

    const payload = {
      ...payloadDefault,
      linkedVehicles: driversSelected.length > 0 ? [linkedVehicleItem] : undefined,
    };

    try {
      const response = await sendTripUpdateCopy(
        driversSelected.length > 0 ? payload : payloadDefault,
        routeData.tripId
      );

      await saveAreaNewRoute(response?.data.result?.newRouteId, areasRework, areasCopyRework);
      Notification.success(`Rota sem otimização editada com sucesso.`);
      dispatch(defineAvoidAreas({ formatted: undefined }));
      dispatch(defineAreasRework([]));
      navigate("/routing/trips");
    } catch (error: any) {
      if (error?.response?.data?.message) {
        Notification.error(error?.response?.data?.message || "Motorista com viagem neste período.");
      } else {
        Notification.error("Não foi possível enviar a viagem.");
      }
    } finally {
      setIsCreatingTrip(false);
    }
  };

  const CreateTripCSVNoSuggestion = async (routeStore: any, suggestion?: string) => {
    if (isCreatingTrip) return;
    setIsCreatingTrip(true);
    try {
      const responseCreate = await createAndSendRoute({
        ...routeStore,
        strategy: suggestion || null,
      });
      await CreateTripNoSuggestion(responseCreate?.data?.result);
    } catch (error: any) {
      if (error?.response?.data?.message) {
        Notification.error(error?.response?.data?.message || "Motorista com viagem neste período.");
      } else {
        Notification.error("Não foi possível enviar a viagem.");
      }
    } finally {
      setIsCreatingTrip(false);
    }
  };

  const handleSkip = async () => {
    if (isCreatingTrip) return;

    // Edit first: !body.id alone must not divert edit into create×3
    // (CreateTripCSVNoSuggestion → createAndSendRoute + updateRouteForTrip + sendTripCustom).
    if (routeData?.tripId || edit) {
      await EditTripNoSuggestion();
      return;
    }

    if (csv || !body?.id) {
      await CreateTripCSVNoSuggestion(routeStore);
      return;
    }

    await CreateTripNoSuggestion();
  };

  const handleBack = useCallback(() => {
    if (navigateTo) {
      navigate(navigateTo.route, navigateTo.params);
      return;
    }
  }, [navigate, navigateTo, routeData, state, smartRoutePreference, body]);

  return {
    loading,
    suggestions,
    smartRoutePreference,
    handleSelectSuggestion,
    handleSkip,
    handleBack,
    routeData,
    edit,
    selectedFuelOption,
    isCreatingTrip,
  };
};

